import { EyeOutlined, FileTextOutlined, CalendarOutlined, DownloadOutlined, ReloadOutlined, CheckOutlined, BankOutlined, FilePdfOutlined, FileExcelOutlined } from '@ant-design/icons';
import { ConfigProvider, DatePicker, Select, Card, Table, Tag, Space, Button, Modal, Tabs, Statistic, Row, Col, Spin, Popconfirm } from 'antd';
import * as XLSX from 'xlsx';
import frFR from "antd/locale/fr_FR";
import { useEffect, useState, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-toastify';
import Footer from '../../../components/Footer';
import NavBar from '../../../components/NavBar';
import SideBar from '../../../components/SideBar';
import axiosInstance from '../../../variables/api/axiosInstance';
import { frenchMonth } from '../../../variables/functions';
import { displayNumbers } from '../../../variables/numbersFunctions';
import BordereauEmissions from './bordereaux/BordereauEmissions';
import BordereauAnnulations from './bordereaux/BordereauAnnulations';
import BordereauSinistresPayes from './bordereaux/BordereauSinistresPayes';
import BordereauRecoursEncaisses from './bordereaux/BordereauRecoursEncaisses';
import CompteDeCession from './bordereaux/CompteDeCession';
import EtatChequesEmis from './documents/EtatChequesEmis';
import { LOGGED_USER } from '../../../variables/constants';
import { useNavigate } from 'react-router-dom';
import ChequeAssignmentModal from './modals/ChequeAssignementModal';

const CessionsPool = () => {
    const [compagnies, setCompagnies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cessionsGroupees, setCessionsGroupees] = useState([]);
    const [selectedPeriode, setSelectedPeriode] = useState(null);
    const [selectedCompagnie, setSelectedCompagnie] = useState(null);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedCessionGroup, setSelectedCessionGroup] = useState(null);
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [cessionDetailsCache, setCessionDetailsCache] = useState({});
    const [cessions, setCessions] = useState([]);
    const [chequeModalVisible, setChequeModalVisible] = useState(false);
    const [selectedCessionForCheque, setSelectedCessionForCheque] = useState(null);
    const [banques, setBanques] = useState([]);
    const [loadingValidation, setLoadingValidation] = useState({});

    // État chèques (impression / export par mois)
    const [chequesEtatModalVisible, setChequesEtatModalVisible] = useState(false);
    const [selectedMoisForChequesEtat, setSelectedMoisForChequesEtat] = useState(null);
    const [isPrintingCheques, setIsPrintingCheques] = useState(false);

    const navigate = useNavigate();

    // Refs pour l'impression
    const printEmissionsRef = useRef();
    const printAnnulationsRef = useRef();
    const printSinistresRef = useRef();
    const printRecoursRef = useRef();
    const printCessionRef = useRef();
    const printChequesEtatRef = useRef();

    // Handlers d'impression
    const handlePrintEmissions = useReactToPrint({
        content: () => printEmissionsRef.current,
    });

    const handlePrintAnnulations = useReactToPrint({
        content: () => printAnnulationsRef.current,
    });

    const handlePrintSinistres = useReactToPrint({
        content: () => printSinistresRef.current,
    });

    const handlePrintRecours = useReactToPrint({
        content: () => printRecoursRef.current,
    });

    const handlePrintCession = useReactToPrint({
        content: () => printCessionRef.current,
    });

    const handlePrintChequesEtat = useReactToPrint({
        content: () => printChequesEtatRef.current,
    });

    /**
     * Construit les lignes de l'état des chèques pour un mois donné.
     * - Une ligne par chèque émis (compagnie + catégorie + N° chèque + banque + montant).
     * - Si une compagnie n'a émis aucun chèque, on ajoute une ligne avec montant = 0.
     */
    const buildChequesRows = (moisGroupe) => {
        if (!moisGroupe?.compagnies) return { rows: [], totalMontant: 0 };

        const rows = [];
        let totalMontant = 0;

        moisGroupe.compagnies.forEach((comp) => {
            const sousAvecCheque = (comp.sousCessions || []).filter((sc) => sc.numeroCheque);

            if (sousAvecCheque.length === 0) {
                rows.push({
                    key: `${comp.key}-no-cheque`,
                    compagnie: comp.compagnie?.raisonSociale || '',
                    categorie: '—',
                    numeroCheque: '—',
                    banque: '—',
                    montantCheque: 0,
                });
            } else {
                sousAvecCheque.forEach((sc) => {
                    const montant = sc.montantCheque || 0;
                    totalMontant += montant;
                    rows.push({
                        key: `${comp.key}-${sc.codeId}`,
                        compagnie: comp.compagnie?.raisonSociale || '',
                        categorie: sc.categorie?.codeCategorie || '—',
                        numeroCheque: sc.numeroCheque || '—',
                        banque: sc.banque || '—',
                        montantCheque: montant,
                    });
                });
            }
        });

        return { rows, totalMontant };
    };

    const ouvrirEtatChequesModal = (moisGroupe) => {
        setSelectedMoisForChequesEtat(moisGroupe);
        setChequesEtatModalVisible(true);
    };

    const fermerEtatChequesModal = () => {
        setChequesEtatModalVisible(false);
        setSelectedMoisForChequesEtat(null);
    };

    const lancerImpressionChequesEtat = () => {
        setIsPrintingCheques(true);
        setTimeout(() => {
            handlePrintChequesEtat();
            setTimeout(() => setIsPrintingCheques(false), 200);
        }, 150);
    };

    const exporterChequesEtatExcel = (moisGroupe) => {
        if (!moisGroupe) return;
        const { rows, totalMontant } = buildChequesRows(moisGroupe);

        const dataExcel = rows.map((row, idx) => ({
            'N°': idx + 1,
            'Compagnie': row.compagnie,
            'Catégorie': row.categorie,
            'N° Chèque': row.numeroCheque,
            'Banque': row.banque,
            'Montant (F CFA)': row.montantCheque,
        }));

        // Ligne de total
        dataExcel.push({
            'N°': '',
            'Compagnie': '',
            'Catégorie': '',
            'N° Chèque': '',
            'Banque': 'TOTAL',
            'Montant (F CFA)': totalMontant,
        });

        const worksheet = XLSX.utils.json_to_sheet(dataExcel);
        worksheet['!cols'] = [
            { wch: 6 },
            { wch: 35 },
            { wch: 12 },
            { wch: 18 },
            { wch: 25 },
            { wch: 18 },
        ];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Etat Chèques');

        const [annee, mois] = (moisGroupe.moisRapport || '').split('-');
        const filename = `Etat_Cheques_${frenchMonth(mois) || mois}_${annee}.xlsx`;
        XLSX.writeFile(workbook, filename);
    };

    useEffect(() => {
        // Charger les catégories et compagnies
        Promise.all([
            axiosInstance.get(`/api/organisme/assurance`),
            axiosInstance.get(`/api/parametre/type/BANQUE`),
        ])
            .then(([compRes, banqueRes]) => {
                setCompagnies(compRes.data);
                console.log("Banques:", banqueRes.data);
                setBanques(banqueRes.data || []);
            })
            .catch((err) => {
                console.error(err);
                toast.error("Erreur lors du chargement des données");
            });

        // Charger les cessions au démarrage
        chargerCessionsValidees();
    }, []);

    useEffect(() => {
        chargerCessionsValidees();
    }, [selectedPeriode, selectedCompagnie]);

    const chargerCessionsValidees = () => {
        setLoading(true);
        const params = {};

        if (selectedPeriode) {
            params.mois = selectedPeriode;
        }

        if (selectedCompagnie) {
            params.company = selectedCompagnie;
        }

        // Appel à l'endpoint optimisé (version légère)
        axiosInstance
            .get(`/api/cession/validees`, { params })
            .then((res) => {
                console.log("Cessions reçues:", res.data);
                setCessions(res.data);
                // Grouper les cessions par mois/année et compagnie
                const grouped = grouperCessions(res.data);
                setCessionsGroupees(grouped);
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                toast.error("Erreur lors du chargement des cessions");
                setLoading(false);
            });
    };

    const grouperCessions = (cessions) => {
        // Niveau 1 : groupement par mois (YYYY-MM)
        // Niveau 2 : à l'intérieur de chaque mois, sous-groupement par compagnie
        const moisGroupes = {};

        cessions.forEach((cession) => {
            const moisRapport = cession?.moisRapport?.substring(0, 7); // Format: YYYY-MM
            const compagnieCode = cession?.compagnie?.code;
            const compKey = `${moisRapport}_${compagnieCode}`;

            // Initialiser le groupe mois
            if (!moisGroupes[moisRapport]) {
                moisGroupes[moisRapport] = {
                    key: moisRapport,
                    moisRapport,
                    dateEmission: cession.dateEmission,
                    compagniesMap: {},
                    totalEmissionsGlobal: 0,
                    totalAnnulationsGlobal: 0,
                    totalSinistresGlobal: 0,
                    totalRecoursGlobal: 0,
                    soldeGlobal: 0,
                    nbSousCessions: 0,
                };
            }

            const moisGroupe = moisGroupes[moisRapport];

            // Initialiser le sous-groupe compagnie
            if (!moisGroupe.compagniesMap[compKey]) {
                moisGroupe.compagniesMap[compKey] = {
                    key: compKey,
                    moisRapport,
                    compagnie: cession.compagnie,
                    dateEmission: cession.dateEmission,
                    sousCessions: [],
                    totalEmissionsGlobal: 0,
                    totalAnnulationsGlobal: 0,
                    totalSinistresGlobal: 0,
                    totalRecoursGlobal: 0,
                    soldeGlobal: 0
                };
            }

            const compGroupe = moisGroupe.compagniesMap[compKey];

            // Ajouter la sous-cession à la compagnie
            compGroupe.sousCessions.push(cession);
            compGroupe.totalEmissionsGlobal += cession.totalEmissions || 0;
            compGroupe.totalAnnulationsGlobal += cession.totalAnnulations || 0;
            compGroupe.totalSinistresGlobal += cession.totalSinistres || 0;
            compGroupe.totalRecoursGlobal += cession.totalRecours || 0;

            // Calcul du solde unitaire
            const debit = (cession.totalAnnulations || 0) + (cession.indemnitesEmissions || 0) + (cession.totalSinistres || 0);
            const credit = (cession.totalEmissions || 0) + (cession.indemnitesAnnulations || 0) + (cession.totalRecours || 0);
            const soldeUnitaire = debit - credit;
            compGroupe.soldeGlobal += soldeUnitaire;

            // Mise à jour des totaux du mois
            moisGroupe.totalEmissionsGlobal += cession.totalEmissions || 0;
            moisGroupe.totalAnnulationsGlobal += cession.totalAnnulations || 0;
            moisGroupe.totalSinistresGlobal += cession.totalSinistres || 0;
            moisGroupe.totalRecoursGlobal += cession.totalRecours || 0;
            moisGroupe.soldeGlobal += soldeUnitaire;
            moisGroupe.nbSousCessions += 1;

            // Conserver la date la plus récente pour le mois
            if (new Date(cession.dateEmission) > new Date(moisGroupe.dateEmission)) {
                moisGroupe.dateEmission = cession.dateEmission;
            }
        });

        // Conversion : compagniesMap → tableau trié + tri des mois (plus récent en premier)
        return Object.values(moisGroupes)
            .map((m) => {
                const compagnies = Object.values(m.compagniesMap).sort((a, b) =>
                    a.compagnie.raisonSociale.localeCompare(b.compagnie.raisonSociale)
                );
                const { compagniesMap, ...rest } = m;
                return {
                    ...rest,
                    compagnies,
                    nbCompagnies: compagnies.length,
                };
            })
            .sort((a, b) => b.moisRapport.localeCompare(a.moisRapport));
    };

    const chargerDetailsCessionByCategorie = async (cession) => {
        // Vérifier si déjà en cache
        if (cessionDetailsCache[cession.codeId]) {
            return cessionDetailsCache[cession.codeId];
        }
        console.log("Chargement des détails de la cession", cession);
        setLoadingDetails(true);
        try {
            console.log("Chargement des détails de la cession hmmmm", cession);
            const response = await axiosInstance.get(`/api/cession/validees/details`, {
                params: {
                    mois: cession.moisRapport,
                    compagnie: cession.compagnie.code,
                    categorie: cession.categorie.codeCategorie
                }
            });
            const details = response.data;
            console.log("Details: ", details);
            // Mettre en cache
            setCessionDetailsCache(prev => ({
                ...prev,
                [cession.categorieCode]: details
            }));

            setLoadingDetails(false);
            return details;
        } catch (err) {
            console.error(err);
            toast.error("Erreur lors du chargement des détails");
            setLoadingDetails(false);
            return null;
        }
    };

    const ouvrirDetailsSousCession = async (cession) => {
        console.log("Ouverture des détails de la sous-cession", cession);
        // Charger les détails complets si nécessaire
        let cessionComplete = cession;

        // Vérifier si les bordereaux sont vides (version légère)
        if (!cession.bordereauEmissions?.items || cession?.bordereauEmissions?.items?.length === 0) {
            cessionComplete = await chargerDetailsCessionByCategorie(cession);
            if (!cessionComplete) return;
        }
        console.log("Cession complete: ", cessionComplete);
        setSelectedCessionGroup(prev => ({
            ...prev,
            cessionActive: cessionComplete
        }));
        setDetailModalVisible(true);
    };

    // Fonction pour valider un chèque
    const validerCheque = async (cession) => {
        setLoadingValidation(prev => ({ ...prev, [cession.codeId]: true }));

        try {
            await axiosInstance.get(`/api/cession/valider-cheque/${cession.codeId}`);
            toast.success('Chèque validé avec succès');

            // Recharger les cessions pour mettre à jour l'affichage
            chargerCessionsValidees();
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || 'Erreur lors de la validation du chèque');
        } finally {
            setLoadingValidation(prev => ({ ...prev, [cession.codeId]: false }));
        }
    };

    const colonnesPrincipales = [
        {
            title: 'Période',
            dataIndex: 'moisRapport',
            key: 'moisRapport',
            render: (mois) => {
                const [annee, moisNum] = mois.split('-');
                return (
                    <Space size={12}>
                        <div style={{
                            width: 40, height: 40, borderRadius: 10,
                            background: 'linear-gradient(135deg, #F39C12 0%, #E67E22 100%)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 3px 8px rgba(243, 156, 18, 0.35)'
                        }}>
                            <CalendarOutlined style={{ color: 'white', fontSize: 18 }} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: 700, fontSize: 15, color: '#2c3e50', lineHeight: 1.2 }}>
                                {frenchMonth(moisNum)}
                            </span>
                            <span style={{ fontSize: 12, color: '#7f8c8d', fontWeight: 500 }}>
                                {annee}
                            </span>
                        </div>
                    </Space>
                );
            },
            width: 200,
            sorter: (a, b) => a.moisRapport.localeCompare(b.moisRapport),
        },
        {
            title: 'Compagnies',
            key: 'nbCompagnies',
            render: (_, record) => (
                <Tag color="geekblue" style={{ fontSize: 13, fontWeight: 600, padding: '4px 12px', borderRadius: 8 }}>
                    {record.nbCompagnies} compagnie{record.nbCompagnies > 1 ? 's' : ''}
                </Tag>
            ),
            align: 'center',
            width: 140,
            sorter: (a, b) => a.nbCompagnies - b.nbCompagnies,
        },
        // {
        //     title: 'Sous-cessions',
        //     key: 'nbSousCessions',
        //     render: (_, record) => (
        //         <Tag color="orange" style={{ fontSize: 13, fontWeight: 600, padding: '4px 12px', borderRadius: 8 }}>
        //             {record.nbSousCessions} catégorie{record.nbSousCessions > 1 ? 's' : ''}
        //         </Tag>
        //     ),
        //     align: 'center',
        //     width: 140,
        //     sorter: (a, b) => a.nbSousCessions - b.nbSousCessions,
        // },
        {
            title: 'Total Émissions',
            dataIndex: 'totalEmissionsGlobal',
            key: 'totalEmissionsGlobal',
            render: (montant) => (
                <span style={{ color: '#52c41a', fontWeight: 700, fontSize: 14 }}>
                    {displayNumbers(montant)} F CFA
                </span>
            ),
            align: 'right',
            sorter: (a, b) => a.totalEmissionsGlobal - b.totalEmissionsGlobal,
        },
        {
            title: 'Total Sinistres',
            dataIndex: 'totalSinistresGlobal',
            key: 'totalSinistresGlobal',
            render: (montant) => (
                <span style={{ color: '#ff4d4f', fontWeight: 700, fontSize: 14 }}>
                    {displayNumbers(montant)} F CFA
                </span>
            ),
            align: 'right',
            sorter: (a, b) => a.totalSinistresGlobal - b.totalSinistresGlobal,
        },
        {
            title: 'Solde Global',
            dataIndex: 'soldeGlobal',
            key: 'soldeGlobal',
            render: (solde) => {
                const isPositif = solde > 0;
                return (
                    <Tag color={isPositif ? 'red' : 'green'} style={{ fontSize: '13px', fontWeight: 700, padding: '6px 12px', borderRadius: 8 }}>
                        {displayNumbers(Math.abs(solde))} F CFA
                        <br />
                        <small style={{ fontWeight: 500 }}>{isPositif ? 'Compagnies' : 'Pool TPV'}</small>
                    </Tag>
                );
            },
            align: 'center',
            sorter: (a, b) => a.soldeGlobal - b.soldeGlobal,
        },
        {
            title: 'Dernière cession',
            dataIndex: 'dateEmission',
            key: 'dateEmission',
            render: (date) => (
                <span style={{ color: '#7f8c8d', fontSize: 12 }}>
                    {new Date(date).toLocaleString('fr-FR')}
                </span>
            ),
            width: 180,
            sorter: (a, b) => new Date(a.dateEmission) - new Date(b.dateEmission),
        },
        {
            title: 'État chèques',
            key: 'etatCheques',
            render: (_, record) => (
                <Button
                    type="primary"
                    icon={<BankOutlined />}
                    onClick={(e) => {
                        e.stopPropagation();
                        ouvrirEtatChequesModal(record);
                    }}
                    style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderColor: '#667eea',
                        boxShadow: '0 2px 6px rgba(102, 126, 234, 0.35)',
                        fontWeight: 600
                    }}
                >
                    État chèques
                </Button>
            ),
            align: 'center',
            width: 170,
            fixed: 'right',
        },
    ];

    const colonnesCompagnies = [
        {
            title: 'Compagnie',
            dataIndex: 'compagnie',
            key: 'compagnie',
            render: (compagnie) => (
                <Space size={10}>
                    <div style={{
                        width: 34, height: 34, borderRadius: 8,
                        background: '#fff7e6',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: '1px solid #ffe7ba'
                    }}>
                        <BankOutlined style={{ color: '#F39C12', fontSize: 15 }} />
                    </div>
                    <span style={{ fontWeight: 600, color: '#2c3e50' }}>{compagnie.raisonSociale}</span>
                </Space>
            ),
            sorter: (a, b) => a.compagnie.raisonSociale.localeCompare(b.compagnie.raisonSociale),
        },
        {
            title: 'Catégories',
            key: 'categories',
            render: (_, record) => (
                <Space wrap>
                    {record.sousCessions.map((sc) => (
                        <Tag color="orange" key={sc.categorieCode}>
                            {sc.categorie.codeCategorie}
                        </Tag>
                    ))}
                </Space>
            ),
        },
        {
            title: 'Émissions',
            dataIndex: 'totalEmissionsGlobal',
            key: 'totalEmissionsGlobal',
            render: (montant) => (
                <span style={{ color: '#52c41a', fontWeight: 600 }}>
                    {displayNumbers(montant)} F CFA
                </span>
            ),
            align: 'right',
            sorter: (a, b) => a.totalEmissionsGlobal - b.totalEmissionsGlobal,
        },
        {
            title: 'Sinistres',
            dataIndex: 'totalSinistresGlobal',
            key: 'totalSinistresGlobal',
            render: (montant) => (
                <span style={{ color: '#ff4d4f', fontWeight: 600 }}>
                    {displayNumbers(montant)} F CFA
                </span>
            ),
            align: 'right',
            sorter: (a, b) => a.totalSinistresGlobal - b.totalSinistresGlobal,
        },
        {
            title: 'Solde',
            dataIndex: 'soldeGlobal',
            key: 'soldeGlobal',
            render: (solde) => {
                const isPositif = solde > 0;
                return (
                    <Tag color={isPositif ? 'red' : 'green'} style={{ fontSize: '13px', fontWeight: 600 }}>
                        {displayNumbers(Math.abs(solde))} F CFA
                        <br />
                        <small>{isPositif ? 'Compagnie' : 'Pool TPV'}</small>
                    </Tag>
                );
            },
            align: 'center',
            sorter: (a, b) => a.soldeGlobal - b.soldeGlobal,
        },
        {
            title: 'Date de cession',
            dataIndex: 'dateEmission',
            key: 'dateEmission',
            render: (date) => new Date(date).toLocaleString('fr-FR'),
            width: 170,
            sorter: (a, b) => new Date(a.dateEmission) - new Date(b.dateEmission),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Button
                    type="primary"
                    icon={<EyeOutlined />}
                    onClick={() => ouvrirDetailsCession(record)}
                    style={{ background: '#F39C12', borderColor: '#F39C12' }}
                >
                    Détails
                </Button>
            ),
            align: 'center',
            width: 120,
            fixed: 'right',
        },
    ];

    const colonnesSousCessions = [
        {
            title: 'Catégorie',
            dataIndex: 'categorie',
            key: 'categorie',
            render: (categorie) => (
                <Tag color="orange" style={{ fontSize: '13px' }}>
                    {categorie.codeCategorie} - {categorie.nom}
                </Tag>
            ),
            width: 100
        },
        {
            title: 'Émissions',
            dataIndex: 'totalEmissions',
            key: 'totalEmissions',
            render: (montant) => (
                <span style={{ color: '#52c41a' }}>
                    {displayNumbers(montant || 0)} F CFA
                </span>
            ),
            align: 'right',
            width: 150,
        },
        {
            title: 'Annulations',
            dataIndex: 'totalAnnulations',
            key: 'totalAnnulations',
            render: (montant) => (
                <span style={{ color: '#faad14' }}>
                    {displayNumbers(montant || 0)} F CFA
                </span>
            ),
            align: 'right',
            width: 150,
        },
        {
            title: 'Sinistres Payés',
            dataIndex: 'totalSinistres',
            key: 'totalSinistres',
            render: (montant) => (
                <span style={{ color: '#ff4d4f' }}>
                    {displayNumbers(montant || 0)} F CFA
                </span>
            ),
            align: 'right',
            width: 150,
        },
        {
            title: 'Recours Encaissés',
            dataIndex: 'totalRecours',
            key: 'totalRecours',
            render: (montant) => (
                <span style={{ color: '#1890ff' }}>
                    {displayNumbers(montant || 0)} F CFA
                </span>
            ),
            align: 'right',
            width: 150,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => {
                const debit = (record.totalAnnulations || 0) + (record.indemnitesEmissions || 0) + (record.totalSinistres || 0);
                const credit = (record.totalEmissions || 0) + (record.indemnitesAnnulations || 0) + (record.totalRecours || 0);
                const solde = debit - credit;
                const soldeEnFaveurPool = solde < 0;

                return (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Button
                            type="link"
                            icon={<FileTextOutlined />}
                            onClick={() => ouvrirDetailsSousCession(record)}
                            style={{ color: '#F39C12' }}
                        >
                            Voir les bordereaux
                        </Button>

                        {soldeEnFaveurPool && LOGGED_USER?.profil?.organisme?.typeOrganisme !== 'POOL' && !record.numeroCheque && (
                            <Button
                                type="primary"
                                icon={<BankOutlined />}
                                onClick={() => ouvrirModalCheque(record)}
                                style={{ background: '#52c41a', borderColor: '#52c41a', width: '100%' }}
                            >
                                Assigner chèque
                            </Button>
                        )}

                        {/* Affichage des informations de chèque si assigné */}
                        {soldeEnFaveurPool && record.numeroCheque && (
                            <div>
                                <div style={{
                                    background: record.validationCheque
                                        ? 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)'
                                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    borderRadius: '10px',
                                    padding: '10px 14px',
                                    color: 'white',
                                    fontSize: '12px',
                                    boxShadow: record.validationCheque
                                        ? '0 3px 10px rgba(82, 196, 26, 0.35)'
                                        : '0 3px 10px rgba(102, 126, 234, 0.35)',
                                    textAlign: 'left',
                                    position: 'relative'
                                }}>
                                    {record.validationCheque && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '8px',
                                            right: '8px',
                                            background: 'rgba(255, 255, 255, 0.3)',
                                            borderRadius: '50%',
                                            padding: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <CheckOutlined style={{ fontSize: '14px', color: 'white' }} />
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                                        <BankOutlined style={{ marginRight: '8px', fontSize: '16px' }} />
                                        <strong style={{ fontSize: '13px' }}>Chèque N° {record.numeroCheque}</strong>
                                    </div>
                                    <div style={{ fontSize: '11px', opacity: 0.95 }}>
                                        🏦 {record.banque}
                                    </div>
                                    <div style={{ fontSize: '11px', opacity: 0.95, marginTop: '2px' }}>
                                        📅 {new Date(record.dateCheque).toLocaleDateString('fr-FR')}
                                    </div>
                                    <div style={{ fontSize: '11px', opacity: 0.95, marginTop: '2px', fontWeight: 600 }}>
                                        💰 {displayNumbers(record.montantCheque)} F CFA
                                    </div>

                                    {record.dateValidationCheque && (
                                        <div style={{
                                            marginTop: '6px',
                                            paddingTop: '6px',
                                            borderTop: '1px solid rgba(255, 255, 255, 0.3)',
                                            fontSize: '10px',
                                            opacity: 0.9
                                        }}>
                                            ✅ Validé par le Pool <br />
                                            {/* Date validation */}🗓️ le {new Date(record.dateValidationCheque).toLocaleDateString('fr-FR')}
                                        </div>
                                    )}
                                </div>

                                {/* Bouton de validation pour le Pool uniquement */}
                                {LOGGED_USER?.profil?.organisme?.typeOrganisme === 'POOL' && !record.dateValidationCheque && (
                                    <Popconfirm
                                        title="Validation du chèque"
                                        description={`Confirmer la réception du chèque N° ${record.numeroCheque} d'un montant de ${displayNumbers(record.montantCheque)} F CFA ?`}
                                        onConfirm={() => validerCheque(record)}
                                        okText="Oui, valider"
                                        cancelText="Annuler"
                                        okButtonProps={{
                                            style: { background: '#52c41a', borderColor: '#52c41a' },
                                            loading: loadingValidation[record.codeId]
                                        }}
                                    >
                                        <Button
                                            type="primary"
                                            icon={<CheckOutlined />}
                                            style={{
                                                background: '#52c41a',
                                                borderColor: '#52c41a',
                                                marginTop: '8px',
                                                width: '100%'
                                            }}
                                            size="small"
                                            loading={loadingValidation[record.codeId]}
                                        >
                                            Valider la réception du chèque
                                        </Button>
                                    </Popconfirm>
                                )}
                            </div>
                        )}
                    </Space>
                );
            },
            align: 'center',
            width: 300,
        },
    ];

    // Niveau le plus profond : sous-cessions (catégories) d'une compagnie
    const expandedCompagnieRender = (record) => {
        return (
            <div style={{ padding: '16px', background: '#fafafa', borderRadius: '8px' }}>
                <h5 style={{ marginBottom: '12px', color: '#F39C12', fontWeight: 600 }}>
                    Sous-cessions par catégorie ({record.sousCessions.length})
                </h5>
                <Table
                    columns={colonnesSousCessions}
                    dataSource={record.sousCessions}
                    pagination={false}
                    rowKey="codeId"
                    size="small"
                    bordered
                />
            </div>
        );
    };

    // Niveau intermédiaire : compagnies pour un mois donné
    const expandedRowRender = (record) => {
        const [annee, moisNum] = record.moisRapport.split('-');
        return (
            <div style={{
                padding: '20px 24px',
                background: 'linear-gradient(180deg, #fff8e7 0%, #fafafa 100%)',
                borderLeft: '4px solid #F39C12'
            }}>
                <h4 style={{
                    marginBottom: '16px',
                    color: '#F39C12',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    fontWeight: 700
                }}>
                    <BankOutlined />
                    Compagnies — {frenchMonth(moisNum)} {annee}
                    <Tag color="geekblue" style={{ marginLeft: 8, fontWeight: 600 }}>
                        {record.nbCompagnies} compagnie{record.nbCompagnies > 1 ? 's' : ''}
                    </Tag>
                </h4>
                <Table
                    columns={colonnesCompagnies}
                    dataSource={record.compagnies}
                    pagination={false}
                    rowKey="key"
                    size="middle"
                    bordered
                    expandable={{
                        expandedRowRender: expandedCompagnieRender,
                    }}
                />
            </div>
        );
    };

    const ouvrirDetailsCession = (groupe) => {
        console.log("groupe: ", groupe);
        setSelectedCessionGroup(groupe);
        setDetailModalVisible(true);
    };

    const calculerTotauxCession = (cession) => {
        const debit = (cession.totalAnnulations || 0) + (cession.indemnitesEmissions || 0) + (cession.totalSinistres || 0);
        const credit = (cession.totalEmissions || 0) + (cession.indemnitesAnnulations || 0) + (cession.totalRecours || 0);
        const solde = debit - credit;

        return {
            totalEmissions: cession.totalEmissions || 0,
            totalAnnulations: cession.totalAnnulations || 0,
            indemnitesEmissions: cession.indemnitesEmissions || 0,
            indemnitesAnnulations: cession.indemnitesAnnulations || 0,
            totalSinistres: cession.totalSinistres || 0,
            totalRecours: cession.totalRecours || 0,
            debit,
            credit,
            solde,
            soldeEnFaveurDe: solde > 0 ? 'Compagnie' : 'Pool TPV',
            soldeMontant: Math.abs(solde)
        };
    };

    const primesColumns = [
        {
            title: "N° Police",
            dataIndex: "numeroPolice",
            key: "numeroPolice",
        },
        {
            title: "Avenant",
            dataIndex: "avenant",
            key: "avenant",
        },
        {
            title: "Assuré",
            dataIndex: "assure",
            key: "assure",
        },
        {
            title: "Effet",
            dataIndex: "effet",
            key: "effet",
            render: (effet) => <span>{new Date(effet).toLocaleDateString()}</span>,
        },
        {
            title: "Échéance",
            dataIndex: "echeance",
            key: "echeance",
            render: (echeance) => <span>{new Date(echeance).toLocaleDateString()}</span>,
        },
        {
            title: "Catégorie",
            dataIndex: "categorie",
            key: "categorie",
        },
        {
            title: "PN Pool",
            dataIndex: "primeACeder",
            key: "primeACeder",
            render: (primeACeder) => <span>{displayNumbers(Math.abs(primeACeder))} F CFA</span>
        },
    ];

    const sinistresColumns = [
        {
            title: "N° Sinistre",
            dataIndex: "numeroSinistre",
            key: "numeroSinistre",
            render: (_, record) => <span>{record?.sinistreNumeroSinistre || record?.sinistreNumSinistre}</span>
        },
        {
            title: "N° Police",
            dataIndex: "numeroPolice",
            key: "numeroPolice",
            render: (_, record) => <span>{record?.sinistreNumPolice || record?.sinistreNumPoliceP}</span>
        },
        {
            title: "Assuré",
            dataIndex: "sinistreSouscripteur",
            key: "sinistreSouscripteur",
        },
        {
            title: "Date de survenance",
            dataIndex: "dateSinistre",
            key: "dateSinistre",
        },
        {
            title: "Catégorie",
            key: "categorie",
            render: (_, record) => <span>{selectedCessionGroup?.cessionActive?.categorieCode}</span>
        },
        {
            title: "Justificatif",
            dataIndex: "justificatif",
            key: "justificatif",
        },
        {
            title: "Montant",
            dataIndex: "montant",
            key: "montant",
            render: (montant) => <span>{displayNumbers(montant)} F CFA</span>
        },
    ];

    const handleExport = () => {
        const data = cessions.map((cession) => {
            const { totalEmissions, totalAnnulations, indemnitesEmissions, indemnitesAnnulations, totalSinistres, totalRecours } = calculerTotauxCession(cession);
            return {
                ...cession,
                totalEmissions,
                totalAnnulations,
                indemnitesEmissions,
                indemnitesAnnulations,
                totalSinistres,
                totalRecours,
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Cessions");
        XLSX.writeFile(workbook, "cessions.xlsx");
    };

    // Fonction pour ouvrir le modal de chèque
    const ouvrirModalCheque = (cession) => {
        // Calculer le solde pour vérifier s'il est en faveur du Pool
        const debit = (cession.totalAnnulations || 0) + (cession.indemnitesEmissions || 0) + (cession.totalSinistres || 0);
        const credit = (cession.totalEmissions || 0) + (cession.indemnitesAnnulations || 0) + (cession.totalRecours || 0);
        const solde = debit - credit;

        if (solde < 0) { // Solde en faveur du Pool
            setSelectedCessionForCheque({
                ...cession,
                soldeMontant: Math.abs(solde)
            });
            setChequeModalVisible(true);
        } else {
            toast.warning("Le chèque ne peut être assigné que si le solde est en faveur du Pool");
        }
    };

    // Fonction de soumission du chèque
    const handleAssignerCheque = async (chequeData) => {
        try {
            await axiosInstance.put('/api/cession/assigner-cheque', chequeData);
            toast.success('Chèque assigné avec succès');
            setChequeModalVisible(false);
            setSelectedCessionForCheque(null);
            chargerCessionsValidees(); // Recharger les données
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || 'Erreur lors de l\'assignation du chèque');
        }
    };

    return (
        <div className="layout-wrapper layout-content-navbar">
            <style>{`
        .pool-header-card {
          background: linear-gradient(135deg, #F39C12 0%, #E67E22 100%);
          border-radius: 16px;
          padding: 32px;
          margin-bottom: 24px;
          box-shadow: 0 4px 20px rgba(243, 156, 18, 0.25);
          color: white;
        }

        .pool-header-card h2 {
          color: white;
          font-size: 28px;
          font-weight: 700;
          margin: 0 0 8px 0;
        }

        .pool-header-card p {
          color: rgba(255, 255, 255, 0.9);
          margin: 0;
          font-size: 15px;
        }

        .pool-filter-card {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          margin-bottom: 24px;
        }

        .pool-stats-card {
          background: white;
          border-radius: 12px;
          padding: 24px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          margin-bottom: 24px;
        }

        .ant-table-expanded-row > td {
          padding: 0 !important;
        }

        .detail-modal .ant-modal-content {
          border-radius: 16px;
        }

        .detail-modal .ant-modal-header {
          background: linear-gradient(135deg, #F39C12 0%, #E67E22 100%);
          border-radius: 16px 16px 0 0;
        }

        .detail-modal .ant-modal-title {
          color: white;
          font-size: 18px;
          font-weight: 600;
        }

        .sous-cession-card {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 16px;
          border: 2px solid #e9ecef;
          transition: all 0.3s ease;
        }

        .sous-cession-card:hover {
          border-color: #F39C12;
          box-shadow: 0 4px 12px rgba(243, 156, 18, 0.15);
        }

        .sous-cession-card.active {
          border-color: #F39C12;
          background: #fff;
          box-shadow: 0 4px 12px rgba(243, 156, 18, 0.15);
        }

        .loading-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          border-radius: 12px;
        }
      `}</style>

            <div className="layout-container">
                <SideBar />

                <div className="layout-page">
                    <NavBar />

                    <div className="content-wrapper" style={{ background: '#f5f5f5' }}>
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* En-tête Pool */}
                            <div className="pool-header-card">
                                <h2>Cessions Validées</h2>
                                <p>Consultation des cessions mensuelles validées par les compagnies d'assurance</p>
                            </div>

                            {/* Filtres */}
                            <div className="pool-filter-card">
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={8}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                                            Période
                                        </label>
                                        <DatePicker
                                            placeholder="Filtrer par mois"
                                            onChange={(_, dateString) => setSelectedPeriode(dateString)}
                                            picker="month"
                                            style={{ width: '100%' }}
                                        />
                                    </Col>
                                    {LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL" && (<Col xs={24} sm={12} md={8}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                                            Compagnie
                                        </label>
                                        <Select
                                            placeholder="Filtrer par compagnie"
                                            style={{ width: "100%" }}
                                            onChange={setSelectedCompagnie}
                                            allowClear
                                            options={compagnies.map((com) => ({
                                                value: com.code,
                                                label: com.raisonSociale,
                                            }))}
                                        />
                                    </Col>)}
                                    <Col xs={24} sm={12} md={8}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                                            &nbsp;
                                        </label>
                                        <Button
                                            type="primary"
                                            icon={<ReloadOutlined />}
                                            onClick={chargerCessionsValidees}
                                            style={{ width: '100%', background: '#F39C12', borderColor: '#F39C12' }}
                                            loading={loading}
                                        >
                                            Actualiser
                                        </Button>
                                    </Col>
                                </Row>
                            </div>

                            {/* Statistiques globales */}
                            {cessionsGroupees.length > 0 && (
                                <div className="pool-stats-card">
                                    <Row gutter={16}>
                                        <Col xs={12} sm={6}>
                                            <Statistic
                                                title="Mois couverts"
                                                value={cessionsGroupees.length}
                                                prefix={<CalendarOutlined />}
                                                valueStyle={{ color: '#F39C12' }}
                                            />
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <Statistic
                                                title="Sous-cessions"
                                                value={cessionsGroupees.reduce((acc, g) => acc + g.nbSousCessions, 0)}
                                                prefix={<FileTextOutlined />}
                                                valueStyle={{ color: '#52c41a' }}
                                            />
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <Statistic
                                                title="Total Émissions"
                                                value={displayNumbers(cessionsGroupees.reduce((acc, g) => acc + g.totalEmissionsGlobal, 0))}
                                                suffix="F CFA"
                                                valueStyle={{ color: '#52c41a', fontSize: '18px' }}
                                            />
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <Statistic
                                                title="Total Sinistres"
                                                value={displayNumbers(cessionsGroupees.reduce((acc, g) => acc + g.totalSinistresGlobal, 0))}
                                                suffix="F CFA"
                                                valueStyle={{ color: '#ff4d4f', fontSize: '18px' }}
                                            />
                                        </Col>
                                    </Row>
                                </div>
                            )}

                            {/* Table des cessions */}
                            <Card
                                title="Liste des cessions validées"
                                extra={
                                    <Space>
                                        <Button onClick={handleExport} icon={<DownloadOutlined />} type="dashed">
                                            Exporter
                                        </Button>
                                    </Space>
                                }
                                style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}
                            >
                                <ConfigProvider locale={frFR}>
                                    <Table
                                        columns={colonnesPrincipales}
                                        dataSource={cessionsGroupees}
                                        loading={loading}
                                        expandable={{
                                            expandedRowRender,
                                            expandedRowKeys,
                                            onExpand: (expanded, record) => {
                                                setExpandedRowKeys(expanded ? [record.key] : []);
                                            },
                                        }}
                                        pagination={{
                                            pageSize: 10,
                                            showSizeChanger: true,
                                            showTotal: (total) => `Total: ${total} cession(s)`,
                                        }}
                                        scroll={{ x: 1200 }}
                                    />
                                </ConfigProvider>
                            </Card>

                        </div>

                        <Footer />
                    </div>
                </div>
            </div>

            {/* Modal de détails */}
            <Modal
                title={
                    selectedCessionGroup && (
                        <span>
                            Détails de la cession - {selectedCessionGroup?.cessionActive?.compagnieNom} - {' '}
                            {frenchMonth(selectedCessionGroup?.cessionActive?.moisRapport?.split('-')?.[1])} {selectedCessionGroup?.cessionActive?.moisRapport?.split('-')?.[0]}
                        </span>
                    )
                }
                open={detailModalVisible}
                onCancel={() => {
                    setDetailModalVisible(false);
                    setSelectedCessionGroup(null);
                }}
                width="95%"
                style={{ top: 20 }}
                footer={null}
                className="detail-modal"
            >
                {selectedCessionGroup && (
                    <div style={{ position: 'relative' }}>
                        {loadingDetails && (
                            <div className="loading-overlay">
                                <Spin size="large" tip="Chargement des détails..." />
                            </div>
                        )}

                        {/* Sélection de sous-cession */}
                        <div style={{ marginBottom: '24px' }}>
                            <h4 style={{ marginBottom: '16px', color: '#F39C12' }}>
                                {selectedCessionGroup?.sousCessions ? "Sélectionner une catégorie" : `Categorie : ${selectedCessionGroup?.cessionActive?.categorieCode}`}
                            </h4>
                            <Row gutter={[16, 16]}>
                                {selectedCessionGroup?.sousCessions?.map((sc) => (
                                    <Col xs={24} sm={12} md={8} lg={6} key={sc.categorieCode}>
                                        <div
                                            className={`sous-cession-card ${selectedCessionGroup?.cessionActive?.categorieCode === sc.categorieCode ? 'active' : ''
                                                }`}
                                            onClick={() => ouvrirDetailsSousCession(sc)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <h5 style={{ margin: '0 0 12px 0', color: '#F39C12' }}>
                                                {sc.categorie.codeCategorie}
                                            </h5>
                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                <div>Émissions: {displayNumbers(sc?.totalEmissions || 0)} F</div>
                                                <div>Sinistres: {displayNumbers(sc?.totalSinistres || 0)} F</div>
                                            </div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </div>

                        {/* Détails de la sous-cession sélectionnée */}
                        {selectedCessionGroup.cessionActive && (
                            <ConfigProvider locale={frFR}>
                                <Tabs
                                    type="card"
                                    items={[
                                        {
                                            label: "Bordereau des émissions",
                                            key: "1",
                                            children: (
                                                <BordereauEmissions
                                                    ref={printEmissionsRef}
                                                    data={selectedCessionGroup.cessionActive.bordereauEmissions?.items || []}
                                                    columns={primesColumns}
                                                    selectedRows={[]}
                                                    onSelectionChange={() => { }}
                                                    onValidate={() => { }}
                                                    onPrint={handlePrintEmissions}
                                                    isValidated={true}
                                                    isDisabled={true}
                                                    loading={false}
                                                    categorie={{ codeCategorie: selectedCessionGroup.cessionActive.categorieCode }}
                                                    periode={selectedCessionGroup?.cessionActive?.moisRapport}
                                                    compagnie={{ raisonSociale: selectedCessionGroup?.cessionActive?.compagnieNom }}
                                                />
                                            ),
                                        },
                                        {
                                            label: "Bordereau des annulations",
                                            key: "2",
                                            children: (
                                                <BordereauAnnulations
                                                    ref={printAnnulationsRef}
                                                    data={selectedCessionGroup.cessionActive.bordereauAnnulations?.items || []}
                                                    columns={primesColumns}
                                                    selectedRows={[]}
                                                    onSelectionChange={() => { }}
                                                    onValidate={() => { }}
                                                    onPrint={handlePrintAnnulations}
                                                    isValidated={true}
                                                    isDisabled={true}
                                                    loading={false}
                                                    categorie={{ codeCategorie: selectedCessionGroup.cessionActive.categorieCode }}
                                                    periode={selectedCessionGroup?.cessionActive?.moisRapport}
                                                    compagnie={{ raisonSociale: selectedCessionGroup?.cessionActive?.compagnieNom }}
                                                />
                                            ),
                                        },
                                        {
                                            label: "Bordereau des sinistres payés",
                                            key: "3",
                                            children: (
                                                <BordereauSinistresPayes
                                                    ref={printSinistresRef}
                                                    data={selectedCessionGroup.cessionActive.bordereauSinistresPayes?.items || []}
                                                    columns={sinistresColumns}
                                                    selectedRows={[]}
                                                    onSelectionChange={() => { }}
                                                    onValidate={() => { }}
                                                    onPrint={handlePrintSinistres}
                                                    isValidated={true}
                                                    isDisabled={true}
                                                    loading={false}
                                                    categorie={{ codeCategorie: selectedCessionGroup.cessionActive.categorieCode }}
                                                    periode={selectedCessionGroup?.cessionActive?.moisRapport}
                                                    compagnie={{ raisonSociale: selectedCessionGroup?.cessionActive?.compagnieNom }}
                                                />
                                            ),
                                        },
                                        {
                                            label: "Bordereau des recours encaissés",
                                            key: "4",
                                            children: (
                                                <BordereauRecoursEncaisses
                                                    ref={printRecoursRef}
                                                    data={selectedCessionGroup.cessionActive.bordereauRecoursEncaisses?.items || []}
                                                    columns={sinistresColumns}
                                                    selectedRows={[]}
                                                    onSelectionChange={() => { }}
                                                    onValidate={() => { }}
                                                    onPrint={handlePrintRecours}
                                                    isValidated={true}
                                                    isDisabled={true}
                                                    loading={false}
                                                    categorie={{ codeCategorie: selectedCessionGroup.cessionActive.categorieCode }}
                                                    periode={selectedCessionGroup?.cessionActive?.moisRapport}
                                                    compagnie={{ raisonSociale: selectedCessionGroup?.cessionActive?.compagnieNom }}
                                                />
                                            ),
                                        },
                                        {
                                            label: "Compte de cession",
                                            key: "5",
                                            children: (
                                                <CompteDeCession
                                                    ref={printCessionRef}
                                                    validationState={{
                                                        emissions: { validated: true, data: selectedCessionGroup.cessionActive.bordereauEmissions?.items || [] },
                                                        annulations: { validated: true, data: selectedCessionGroup.cessionActive.bordereauAnnulations?.items || [] },
                                                        sinistresPayes: { validated: true, data: selectedCessionGroup.cessionActive.bordereauSinistresPayes?.items || [] },
                                                        recoursEncaisses: { validated: true, data: selectedCessionGroup.cessionActive.bordereauRecoursEncaisses?.items || [] },
                                                        cessionValidated: true,
                                                    }}
                                                    cessionExistante={selectedCessionGroup.cessionActive}
                                                    totals={calculerTotauxCession(selectedCessionGroup.cessionActive)}
                                                    onValidate={() => { }}
                                                    onPrint={handlePrintCession}
                                                    isValidated={true}
                                                    isDisabled={true}
                                                    loading={false}
                                                    categorie={{ codeCategorie: selectedCessionGroup.cessionActive.categorieCode }}
                                                    periode={selectedCessionGroup?.cessionActive?.moisRapport}
                                                    compagnie={{ raisonSociale: selectedCessionGroup?.cessionActive?.compagnieNom }}
                                                />
                                            ),
                                        },
                                    ]}
                                />
                            </ConfigProvider>
                        )}
                    </div>
                )}
            </Modal>
            {/* Modal État des chèques émis (par mois) */}
            <Modal
                title={
                    selectedMoisForChequesEtat && (
                        <Space>
                            <BankOutlined style={{ color: '#fff' }} />
                            <span>
                                État des chèques émis — {frenchMonth(selectedMoisForChequesEtat.moisRapport?.split('-')?.[1])} {selectedMoisForChequesEtat.moisRapport?.split('-')?.[0]}
                            </span>
                        </Space>
                    )
                }
                open={chequesEtatModalVisible}
                onCancel={fermerEtatChequesModal}
                width={1100}
                footer={null}
                className="detail-modal"
            >
                {selectedMoisForChequesEtat && (() => {
                    const { rows, totalMontant } = buildChequesRows(selectedMoisForChequesEtat);
                    const colsApercu = [
                        {
                            title: 'N°',
                            key: 'index',
                            render: (_, __, idx) => idx + 1,
                            width: 60,
                            align: 'center',
                        },
                        {
                            title: 'Compagnie',
                            dataIndex: 'compagnie',
                            key: 'compagnie',
                            render: (val) => <strong style={{ color: '#2c3e50' }}>{val}</strong>,
                        },
                        {
                            title: 'Catégorie',
                            dataIndex: 'categorie',
                            key: 'categorie',
                            align: 'center',
                            width: 110,
                            render: (val) => val !== '—'
                                ? <Tag color="orange">{val}</Tag>
                                : <span style={{ color: '#bbb' }}>—</span>,
                        },
                        {
                            title: 'N° Chèque',
                            dataIndex: 'numeroCheque',
                            key: 'numeroCheque',
                            render: (val) => val !== '—'
                                ? <span style={{ fontWeight: 600 }}>{val}</span>
                                : <span style={{ color: '#bbb' }}>—</span>,
                        },
                        {
                            title: 'Banque',
                            dataIndex: 'banque',
                            key: 'banque',
                            render: (val) => val !== '—'
                                ? <Space><BankOutlined style={{ color: '#F39C12' }} />{val}</Space>
                                : <span style={{ color: '#bbb' }}>—</span>,
                        },
                        {
                            title: 'Montant',
                            dataIndex: 'montantCheque',
                            key: 'montantCheque',
                            align: 'right',
                            render: (val) => (
                                <span style={{ color: val > 0 ? '#52c41a' : '#bbb', fontWeight: 700 }}>
                                    {displayNumbers(val || 0)} F CFA
                                </span>
                            ),
                        },
                    ];

                    return (
                        <div>
                            {/* Bandeau résumé */}
                            <div style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                borderRadius: 12,
                                padding: '18px 22px',
                                color: 'white',
                                marginBottom: 18,
                                boxShadow: '0 4px 14px rgba(102, 126, 234, 0.35)'
                            }}>
                                <Row gutter={16} align="middle">
                                    <Col xs={24} sm={12}>
                                        <div style={{ fontSize: 12, opacity: 0.85, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Période
                                        </div>
                                        <div style={{ fontSize: 18, fontWeight: 700 }}>
                                            {frenchMonth(selectedMoisForChequesEtat.moisRapport?.split('-')?.[1])} {selectedMoisForChequesEtat.moisRapport?.split('-')?.[0]}
                                        </div>
                                        <div style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>
                                            {selectedMoisForChequesEtat.nbCompagnies} compagnie{selectedMoisForChequesEtat.nbCompagnies > 1 ? 's' : ''} — {rows.filter(r => r.numeroCheque !== '—').length} chèque{rows.filter(r => r.numeroCheque !== '—').length > 1 ? 's' : ''}
                                        </div>
                                    </Col>
                                    <Col xs={24} sm={12} style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: 12, opacity: 0.85, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            Total Chèques
                                        </div>
                                        <div style={{ fontSize: 24, fontWeight: 800 }}>
                                            {displayNumbers(totalMontant)} F CFA
                                        </div>
                                    </Col>
                                </Row>
                            </div>

                            {/* Aperçu */}
                            <Table
                                columns={colsApercu}
                                dataSource={rows}
                                rowKey="key"
                                pagination={false}
                                size="middle"
                                bordered
                                summary={() => (
                                    <Table.Summary fixed>
                                        <Table.Summary.Row style={{ background: '#fafafa' }}>
                                            <Table.Summary.Cell index={0} colSpan={5} align="right">
                                                <strong>TOTAL</strong>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={1} align="right">
                                                <strong style={{ color: '#52c41a' }}>
                                                    {displayNumbers(totalMontant)} F CFA
                                                </strong>
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    </Table.Summary>
                                )}
                            />

                            {/* Boutons d'export */}
                            <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                                <Button
                                    icon={<FileExcelOutlined />}
                                    onClick={() => exporterChequesEtatExcel(selectedMoisForChequesEtat)}
                                    style={{
                                        background: '#1f7a3f',
                                        borderColor: '#1f7a3f',
                                        color: 'white',
                                        fontWeight: 600
                                    }}
                                    size="large"
                                >
                                    Exporter Excel
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<FilePdfOutlined />}
                                    onClick={lancerImpressionChequesEtat}
                                    style={{
                                        background: '#c0392b',
                                        borderColor: '#c0392b',
                                        fontWeight: 600
                                    }}
                                    size="large"
                                >
                                    Imprimer / PDF
                                </Button>
                            </div>

                            {/* Version cachée pour impression PDF */}
                            <div style={{
                                position: isPrintingCheques ? 'relative' : 'absolute',
                                left: isPrintingCheques ? 'auto' : '-9999px',
                                visibility: isPrintingCheques ? 'visible' : 'hidden'
                            }}>
                                <style>
                                    {`
                                        @media print {
                                            @page {
                                                size: portrait;
                                                margin: 10mm;
                                            }
                                            body {
                                                print-color-adjust: exact;
                                                -webkit-print-color-adjust: exact;
                                            }
                                            strong, b, th, .fw-bold {
                                                font-weight: bold !important;
                                            }
                                        }
                                    `}
                                </style>
                                <EtatChequesEmis
                                    ref={printChequesEtatRef}
                                    periode={selectedMoisForChequesEtat.moisRapport}
                                    rows={rows}
                                    totalMontant={totalMontant}
                                />
                            </div>
                        </div>
                    );
                })()}
            </Modal>

            {chequeModalVisible && <ChequeAssignmentModal visible={chequeModalVisible} onCancel={() => setChequeModalVisible(false)} onSubmit={handleAssignerCheque} cessionData={selectedCessionForCheque} banques={banques} />}
            <div className="layout-overlay layout-menu-toggle"></div>
            {LOGGED_USER?.profil?.organisme?.typeOrganisme !== "POOL" && (
                <div class="buy-now">
                    <button
                        onClick={() => navigate('/cession')}
                        class="btn btn-secondary btn-buy-now"
                        style={{
                            backgroundColor: '#242166',
                            borderColor: '#242166',
                            fontWeight: 500,
                            fontSize: '15px',
                            padding: '9px 24px',
                            borderRadius: '10px',
                            boxShadow: '0 6px 20px rgba(36, 33, 102, 0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <CheckOutlined style={{ fontSize: '18px' }} />
                        Valider une cession
                    </button>
                </div>
            )}
            <div className="drag-target"></div>
        </div>
    );
};

export default CessionsPool;